import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useMemo } from "react";
import { InlineEventLinkPreview } from "@/components/common/InlineEventLinkPreview";
import {
  type LinkRendererArgs,
  MarkdownRenderer,
} from "@/components/common/MarkdownRenderer";
import { PreviewSection } from "@/components/common/PreviewSection";
import { useEventLinkPreviewContext } from "@/contexts/EventLinkPreviewContext";
import { useFileGuids } from "@/hooks/useFileGuids";
import { useMarkdownContent } from "@/hooks/useMarkdownContent";
import { useMentionLinkProcessor } from "@/hooks/useMentionLinkProcessor";
import { isInternalEventLink } from "@/lib/utils/linkUtils";
import { findFirstStreamPreviewItem } from "@/lib/utils/streamPreviewUrl";
import { extractLinksFromMarkdown } from "@/lib/utils/urlUtils";

interface CustomMarkdownProps {
  content: string;
  showLinkPreviews?: boolean;
  enableEmojiShortcodeCompat?: boolean;
}

export const CustomMarkdown: React.FC<CustomMarkdownProps> = ({
  content,
  showLinkPreviews = true,
  enableEmojiShortcodeCompat = false,
}) => {
  const fileGuids = useFileGuids(content);
  const markdownContent = useMarkdownContent(content);
  const { processMentionLinks } = useMentionLinkProcessor();
  const { onEventClick } = useEventLinkPreviewContext();

  // React Queryを使用してメンション処理を管理
  const { data: mentionProcessedContent = markdownContent } = useQuery({
    queryKey: ["mention-processing", markdownContent],
    queryFn: () => processMentionLinks(markdownContent),
    enabled: !!markdownContent,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュ
  });

  // 通常のプレビューリンクの取得（内部イベントリンク以外）
  const previewItem = useMemo(() => {
    if (!showLinkPreviews || !mentionProcessedContent) return null;
    return findFirstStreamPreviewItem(
      extractLinksFromMarkdown(mentionProcessedContent),
    );
  }, [mentionProcessedContent, showLinkPreviews]);

  const renderLink = useCallback(
    ({ href }: LinkRendererArgs) => {
      if (!href || !isInternalEventLink(href)) {
        return undefined;
      }

      return <InlineEventLinkPreview url={href} onEventClick={onEventClick} />;
    },
    [onEventClick],
  );

  return (
    <div className="prose max-w-none break-words">
      {/* マークダウンコンテンツ */}
      <MarkdownRenderer
        content={mentionProcessedContent || ""}
        renderLink={renderLink}
        enableEmojiShortcodeCompat={enableEmojiShortcodeCompat}
      />

      {/* 通常のリンクプレビュー */}
      {showLinkPreviews && (
        <PreviewSection previewItem={previewItem} fileGuids={fileGuids} />
      )}
    </div>
  );
};
